/* eslint-disable max-lines */
import { isEmpty } from 'lodash';
import { GoogleGAMAd } from '../../../common/components/GoogleGAMAd/GoogleGAMAd';
import {
  FilterChoice,
  SearchFlags,
  SPECIALTY_DENTIST,
  SPECIALTY_GENERAL_PRACTICE,
} from '../../../common/components/SearchHeader/types/FilterChoice';
import { FreeTextSearch } from '../../../common/schema/hannibal/types.generated';
import { useMobile } from '../../../common/services/screenSize/ScreenSizeService';
import { env } from '../../../common/util/env';
import { filterNulls } from '../../../common/util/filterNulls';
import { ExtendedSearch } from '../../ExtendedSearch/ExtendedSearch';
import { GENERAL_PRACTICE } from '../../constants';
import { useSeoContent } from '../../page-seo/SeoContentProvider';
import { InstantScriptsInlineAd } from '../../search-ad/InstantScriptsInlineAd/InstantScriptsInlineAd';
import { SearchResultItem } from '../../types';
import { useProfilesOnly } from '../../useProfilesOnly';
import { Sidebar } from '../Sidebar/Sidebar';
import { CardsContainer, SidebarContainer } from '../common.styles';
import { ProfileCards, resultType } from './ProfileCards/ProfileCards';
import { SearchInfobox } from './ProfileCards/SearchInfobox/SearchInfobox';
import { ResultCount } from './ResultCount/ResultCount';
import {
  AdsContainer,
  AdventureTime,
  Count,
  ResultListContainer,
  ResultListView,
  Results,
  ResultsContainer,
} from './ResultList.styles';
import {
  TelehealthActions,
  TelehealthActionsVariations,
} from './TelehealthActions/TelehealthActions';

interface Props {
  fetchingMore?: boolean;
  filters: FilterChoice;
  loading?: boolean;
  onClickMore: VoidFunction;
  results: SearchResultItem[];
  searchSpecialty?: string;
  telehealthSearchCardsVariant?: TelehealthActionsVariations;
  totalCount: number;
  headerText?: string;
  extendedResults?: FreeTextSearch;
  extendedFetchMore?: VoidFunction;
  extendedFetchingMore?: boolean;
}

const shouldShowTeleActionCards = (filters: FilterChoice, variant?: string) => {
  if (
    !variant ||
    filters.hideTeleAggregators ||
    filters.topic?.bulkBillingGp ||
    filters.topic?.specialist !== SPECIALTY_GENERAL_PRACTICE ||
    filters.gender ||
    filters.language ||
    filters.paymentMethod ||
    filters.flag !== 'telehealth'
  ) {
    return false;
  }

  return true;
};

/**
 * This function checks that only the Specialty, Book Online, Location, and Telehealth filters are enabled.
 * If any other filter is enabled then false is returned.
 * See logic in https://hejira.atlassian.net/browse/ONION-240
 *
 * @param filters is the applied filters object
 * @returns boolean depending on enabled filters
 */
const displayTeleAggregatorCards = (filters: FilterChoice, searchSpecialty?: string): boolean => {
  const otherFiltersAreDisabled = !Object.entries(filters).some(([filterKey, value]) => {
    // Allow undefined or false filters
    if (value === undefined || value === false) return false;
    // Allow telehealth filter
    if (filterKey === 'flag' && value === SearchFlags.TELEHEALTH) return false;
    // Allow GP only and no other filters in topic
    if (
      filterKey === 'topic' &&
      filters.topic &&
      Object.keys(filters.topic).length === 1 &&
      filters.topic?.specialist === 'GP'
    )
      return false;
    // Allow Book Online without time
    if (filterKey === 'onlineAppointments' && isEmpty(value)) return false;
    // Allow Location, Text Search, and other query parameters
    if (
      filterKey === 'location' ||
      filterKey === 'text' ||
      filterKey === 'alwaysShowFilters' ||
      filterKey === 'extraQuery'
    )
      return false;
    return true;
  });

  return (
    (searchSpecialty === GENERAL_PRACTICE || searchSpecialty === undefined) &&
    filters.flag === SearchFlags.TELEHEALTH &&
    otherFiltersAreDisabled
  );
};

export const ResultList = ({
  fetchingMore = false,
  filters,
  loading = false,
  onClickMore,
  results,
  searchSpecialty,
  telehealthSearchCardsVariant,
  totalCount,
  headerText,
  extendedResults,
  extendedFetchMore,
  extendedFetchingMore,
}: Props) => {
  const mobile = useMobile();
  const profiles = useProfilesOnly(results);

  const preferredProviderText =
    filters.topic?.specialist === SPECIALTY_DENTIST && filters.insurance
      ? `${filters.insurance} preferred provider `
      : '';

  const visualHeader = useSeoContent()?.visualHeader;

  const telehealthActionsVisible = shouldShowTeleActionCards(filters, telehealthSearchCardsVariant);
  const displayInlineAd = displayTeleAggregatorCards(filters, searchSpecialty);

  return (
    <ResultListView>
      <ResultsContainer>
        <CardsContainer>
          <SearchInfobox filters={filters} />

          {telehealthActionsVisible && (
            <TelehealthActions variation={telehealthSearchCardsVariant} />
          )}

          {headerText && <AdventureTime>{headerText}</AdventureTime>}
          {!headerText && (
            <AdventureTime>
              {preferredProviderText}
              {preferredProviderText.length > 0 && visualHeader
                ? visualHeader.charAt(0).toLowerCase() + visualHeader.slice(1)
                : visualHeader}
              {totalCount > 1 && filters.text && (
                <Count>
                  {totalCount.toLocaleString()} results for "{filters.text}"
                </Count>
              )}
            </AdventureTime>
          )}

          <ResultListContainer>
            <Results isLoading={loading}>
              {displayInlineAd && (
                <InstantScriptsInlineAd resultType={resultType(false)} index={0} />
              )}
              <ProfileCards
                profiles={results}
                searchSpecialty={searchSpecialty}
                isExtendedSearch={false}
                searchSuburb={filters.location?.suburb?.name}
              />
            </Results>

            {extendedResults && extendedFetchMore && (
              <>
                <ExtendedSearch
                  primaryResults={filterNulls<string>(results.map(it => it.id ?? null))}
                  data={extendedResults}
                  fetchMore={extendedFetchMore}
                  fetchingMore={extendedFetchingMore ?? false}
                />
              </>
            )}

            {mobile && (
              <AdsContainer>
                <GoogleGAMAd
                  adUnit="HealthEngine.web/results"
                  slotId="gam_mobmrec_pos3"
                  sizes={[[300, 250]]}
                  targetingArguments={{
                    pos: 3,
                    env: env.GAM_ENVIRONMENT,
                  }}
                />
              </AdsContainer>
            )}

            <ResultCount
              shown={profiles.length}
              total={totalCount}
              onClickMore={onClickMore}
              fetchingMore={fetchingMore}
              isExtendedSearch={false}
            />
          </ResultListContainer>
        </CardsContainer>
        {!mobile && (
          <SidebarContainer>
            <Sidebar filters={filters} totalSearchResults={totalCount} />
          </SidebarContainer>
        )}
      </ResultsContainer>
    </ResultListView>
  );
};
